import React, { useMemo, useState } from 'react';
import { Button, Modal } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import {
  CreateMassiveResponse,
  downloadErrorReport,
  UploadOptions,
} from 'services/MassiveService';
import { useCreateMassive } from 'queries/useMassiveQuery';
import MassiveUploadForm, { Option, UploadFormValues } from './uploadForm';
import MassiveUploadResult from './uploadResult';
import { MassiveScope, needsMarketplace, storeIsRequired } from './options';
import { downloadOrNotify } from './download';
import { notifyMassiveError } from './serviceError';

interface Props {
  open: boolean;
  scope: MassiveScope;
  marketplaces: Option[];
  stores: Option[];
  storesLoading?: boolean;
  onMarketplaceChange?: (marketplaceId?: number) => void;
  onClose: () => void;
  onCreated?: (massiveId: string) => void;
}

const emptyValues: UploadFormValues = {
  allowPartial: false,
};

const MassiveUploadModal = ({
  open,
  scope,
  marketplaces,
  stores,
  storesLoading,
  onMarketplaceChange,
  onClose,
  onCreated,
}: Props) => {
  const [values, setValues] = useState<UploadFormValues>(emptyValues);
  const [file, setFile] = useState<UploadFile | undefined>();
  const [result, setResult] = useState<CreateMassiveResponse | undefined>();

  const createMassive = useCreateMassive();

  const options = useMemo<UploadOptions | undefined>(() => {
    if (!file) return undefined;
    return {
      file: file as unknown as File,
      marketplaceId: values.marketplaceId,
      storeId: values.storeId,
      allowPartial: values.allowPartial,
      defaultShipmentType: values.defaultShipmentType,
      defaultDeliveryTerm: values.defaultDeliveryTerm,
    };
  }, [file, values]);

  const missingSelection =
    (needsMarketplace(scope) && !values.marketplaceId) ||
    (storeIsRequired(scope) && !values.storeId);

  const reset = () => {
    setValues(emptyValues);
    setFile(undefined);
    setResult(undefined);
  };

  const close = () => {
    if (createMassive.isPending) return;
    reset();
    onClose();
  };

  const submit = async () => {
    if (!options) return;
    try {
      setResult(await createMassive.mutateAsync(options));
    } catch (error) {
      notifyMassiveError(error, 'Falló al crear');
    }
  };

  const handleMarketplaceChange = (next: UploadFormValues) => {
    if (next.marketplaceId !== values.marketplaceId) {
      onMarketplaceChange?.(next.marketplaceId);
    }
    setValues(next);
  };

  const downloadRejected = () => {
    if (!options) return;
    downloadOrNotify(
      () => downloadErrorReport(options),
      `${result?.massive_id ?? 'masiva'}-rechazadas.xlsx`,
    );
  };

  const stepOneFooter = [
    <Button key="cancel" onClick={close}>
      Cancelar
    </Button>,
    <Button
      key="submit"
      type="primary"
      loading={createMassive.isPending}
      disabled={!file || missingSelection}
      onClick={submit}
    >
      Validar y crear
    </Button>,
  ];

  const stepTwoFooter = [
    result?.rejected && values.allowPartial ? (
      <Button
        key="download"
        icon={<DownloadOutlined />}
        onClick={downloadRejected}
      >
        Descargar rechazadas (.xlsx)
      </Button>
    ) : null,
    <Button key="close" onClick={close}>
      Cerrar
    </Button>,
    result?.massive_id ? (
      <Button
        key="view"
        type="primary"
        onClick={() => {
          onCreated?.(result.massive_id as string);
          close();
        }}
      >
        Ver la masiva
      </Button>
    ) : null,
  ].filter(Boolean);

  return (
    <Modal
      open={open}
      width={760}
      destroyOnClose
      maskClosable={!createMassive.isPending}
      closable={!createMassive.isPending}
      onCancel={close}
      title={
        result
          ? 'Resultado de la validación · Paso 2 de 2'
          : 'Nueva alta masiva · Paso 1 de 2'
      }
      footer={result ? stepTwoFooter : stepOneFooter}
    >
      {result ? (
        <MassiveUploadResult
          result={result}
          allowPartial={values.allowPartial}
        />
      ) : (
        <MassiveUploadForm
          scope={scope}
          values={values}
          onChange={handleMarketplaceChange}
          marketplaces={marketplaces}
          stores={stores}
          storesLoading={storesLoading}
          file={file}
          onFileChange={setFile}
        />
      )}
    </Modal>
  );
};

export default MassiveUploadModal;
